import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { api, Match, Goal, Weather } from '../api/client';
import { Flag, refDisplay } from './TeamBadge';
import { venue } from '../lib/venues';
import { weatherInfo } from '../lib/weather';

// Pizarra de estadio a pantalla COMPLETA para un partido EN VIVO (dispositivo vertical):
// un equipo arriba, el otro abajo, y al medio una franja de color con el minuto bien grande,
// la sede y el clima del lugar. ✕ vuelve a la ficha.
export function StadiumBoard({ match, onClose }: { match: Match; onClose: () => void }) {
  const t1 = refDisplay(match.team1);
  const t2 = refDisplay(match.team2);
  const [a, b] = match.score?.ft ?? [0, 0];
  const v = venue(match.ground);

  const [weather, setWeather] = useState<Weather | null>(null);
  useEffect(() => {
    if (!v) return;
    api.weather(v.lat, v.lon).then(setWeather).catch(() => {});
  }, [v?.ground, v]);

  const w = weather?.ok && weather.code != null ? weatherInfo(weather.code) : null;

  return (
    <Board>
      <CloseX onClick={onClose} aria-label="Cerrar">
        ✕
      </CloseX>

      <Half $pos="top">
        <div className="row">
          <div className="team">
            <Flag team={t1.team} size={72} />
            <span className="nm">{t1.text}</span>
          </div>
          <div className="sc">{a}</div>
        </div>
        <Scorers goals={match.goals1} />
      </Half>

      <Franja>
        <div className="live">
          <span className="dot" /> EN VIVO
        </div>
        <div className="min">{match.live?.clock ?? "—'"}</div>
        {v && (
          <div className="venue">
            📍 {v.stadium} · {v.city} {v.flag}
          </div>
        )}
        {w && (
          <div className="wx">
            {w.emoji} {weather!.temp}°<span className="wxt"> {w.text}</span>
          </div>
        )}
      </Franja>

      <Half $pos="bottom">
        <div className="row">
          <div className="team">
            <Flag team={t2.team} size={72} />
            <span className="nm">{t2.text}</span>
          </div>
          <div className="sc">{b}</div>
        </div>
        <Scorers goals={match.goals2} />
      </Half>
    </Board>
  );
}

function Scorers({ goals }: { goals: Goal[] }) {
  if (goals.length === 0) return null;
  return (
    <div className="scorers">
      {goals.map((g, i) => (
        <span className="g" key={i}>
          ⚽ {g.minute ?? ''}' {g.name}
          {g.penalty ? ' (pen)' : g.owngoal ? ' (e/c)' : ''}
        </span>
      ))}
    </div>
  );
}

const Board = styled.div`
  position: fixed;
  inset: 0;
  z-index: 900;
  display: flex;
  flex-direction: column;
  color: #fff;
  overflow: hidden;
`;

const Half = styled.div<{ $pos: 'top' | 'bottom' }>`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  padding: 18px 20px;
  /* Noche de estadio + foco de luz desde el lado del separador. */
  background: ${({ $pos }) =>
    $pos === 'top'
      ? 'radial-gradient(120% 80% at 50% 100%, rgba(26,168,106,0.22), transparent 65%), linear-gradient(180deg, #03090d, #06160f)'
      : 'radial-gradient(120% 80% at 50% 0%, rgba(26,168,106,0.22), transparent 65%), linear-gradient(0deg, #03090d, #06160f)'};

  /* Bandera + nombre a la IZQUIERDA, marcador a la DERECHA. */
  .row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 14px;
  }
  .team {
    display: flex;
    align-items: center;
    gap: 14px;
    min-width: 0;
  }
  .nm {
    font-weight: 800;
    font-size: 1.5rem;
    letter-spacing: 0.3px;
    text-align: left;
  }
  .sc {
    font-size: 7rem;
    font-weight: 900;
    line-height: 0.9;
    font-variant-numeric: tabular-nums;
    text-shadow: 0 4px 22px rgba(0, 0, 0, 0.5);
    flex-shrink: 0;
  }
  .scorers {
    display: flex;
    flex-wrap: wrap;
    gap: 4px 14px;
    font-size: 0.95rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const Franja = styled.div`
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 12px 16px;
  background: linear-gradient(90deg, ${({ theme }) => theme.colors.accentDark}, ${({ theme }) => theme.colors.accent});
  box-shadow: 0 0 30px rgba(0, 0, 0, 0.55);

  .live {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 1.5px;
    color: #fff;
  }
  .live .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #ff4d4d;
    animation: boardPulse 1.2s infinite;
  }
  .min {
    font-size: 3rem;
    font-weight: 900;
    line-height: 1;
    font-variant-numeric: tabular-nums;
    text-shadow: 0 2px 10px rgba(0, 0, 0, 0.4);
  }
  /* Sede x2 y clima x4 respecto del tamaño base, por pedido. */
  .venue {
    font-size: 1.8rem;
    font-weight: 700;
    text-align: center;
    margin-top: 4px;
  }
  .wx {
    font-size: 3.4rem;
    font-weight: 800;
    line-height: 1.05;
    margin-top: 4px;
    white-space: nowrap;
  }
  .wx .wxt { font-size: 1.4rem; font-weight: 600; opacity: 0.92; }
  @keyframes boardPulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.2; }
  }
`;

const CloseX = styled.button`
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 12px);
  right: 14px;
  z-index: 2;
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #fff;
  border-radius: 999px;
  width: 40px;
  height: 40px;
  font-size: 1.15rem;
`;
